<template>
  <div class="dsa-login">
    <div v-if="submitting || sending" class="dsa-login-overlay">
      <dsa-loading :text="submitting ? 'Signing in…' : 'Sending code…'" />
    </div>
    <div class="dsa-login-card">
      <div class="brand">
        <img
          src="https://www.dsaexam.com/images/5.png?v=2"
          alt="Deep Scholastic Assessment"
          class="brand-logo"
        >
      </div>
      <p class="subtitle">Sign in to continue</p>

      <div class="tabs">
        <button
          type="button"
          class="tab"
          :class="{ active: form.type === '学生' }"
          @click="switchType('学生')"
        >Student</button>
        <button
          type="button"
          class="tab"
          :class="{ active: form.type === 'Institution' }"
          @click="switchType('Institution')"
        >Institution</button>
      </div>

      <div class="field">
        <label>Email</label>
        <input
          v-model="form.account_name"
          type="email"
          autocomplete="username"
          :placeholder="emailPlaceholder"
          @keyup.enter="submit"
        >
      </div>

      <!-- Student: PIN -->
      <div v-if="form.type === '学生'" class="field">
        <label>Verification Code</label>
        <div class="pin-row">
          <input
            v-model="form.account_pwd"
            type="text"
            autocomplete="one-time-code"
            placeholder="Enter code"
            @keyup.enter="submit"
          >
          <button
            type="button"
            class="btn-send"
            :disabled="remainingTime !== 60 || sending"
            @click="sendCode"
          >{{ remainingTime === 60 ? 'Send Code' : remainingTime + 's' }}</button>
        </div>
      </div>

      <!-- Institution / Admin: password -->
      <div v-else class="field">
        <label>Password</label>
        <input
          v-model="form.account_pwd"
          type="password"
          autocomplete="current-password"
          placeholder="Enter password"
          @keyup.enter="submit"
        >
      </div>

      <button type="button" class="btn-login" :disabled="submitting" @click="submit">
        {{ submitting ? 'Signing in…' : 'Log In' }}
      </button>

      <p class="hint">{{ typeHint }}</p>
    </div>
  </div>
</template>

<script>
import { createLoginPinCode, verifyLoginPin, loginByAccountAdmin } from '@/api/dsa'
import {
  setUserId,
  setStudentEmail,
  setUserName,
  setUserType,
  setAccountName,
  setInstitutionAccountId,
  setInstitutionAdvisorId,
  logout,
} from '../../utils/store'

export default {
  name: 'Login',
  data() {
    return {
      form: {
        account_name: '',
        account_pwd: '',
        type: '学生',
      },
      remainingTime: 60,
      timer: null,
      sending: false,
      submitting: false,
    }
  },
  computed: {
    emailPlaceholder() {
      if (this.form.type === 'Institution') return 'Institution contact email'
      return 'Student email'
    },
    typeHint() {
      if (this.form.type === '学生') return 'Students sign in with email + verification code.'
      return 'Institutions sign in with email and password.'
    },
  },
  mounted() {
    logout()
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    switchType(type) {
      this.form.type = type
      this.form.account_pwd = ''
    },
    submit() {
      if (!this.form.account_name) {
        this.$message.error('Please input email')
        return
      }
      if (!this.form.account_pwd) {
        this.$message.error(this.form.type === '学生' ? 'Please input verification code' : 'Please input password')
        return
      }

      if (this.form.type === 'Institution') {
        this.submitting = true
        loginByAccountAdmin(this.form.account_name, this.form.account_pwd)
          .then(res => {
            if (res.data.code === 0) {
              const account = res.data.data
              setInstitutionAccountId(account.account_id || account.id || (account._id && account._id.$id))
              setInstitutionAdvisorId(account.contact_id || account.advisor_id || '')
              setAccountName(account.account_nameEN || account.account_name || this.form.account_name)
              if (account.contact_name) {
                setUserName(account.contact_name)
              }
              setUserType('institution')
              this.$message({ message: 'Login Successful', type: 'success' })
              this.$router.push('/institution/dashboard')
            } else {
              this.$message.warning(res.data.msg || 'Login failed')
            }
          })
          .finally(() => { this.submitting = false })
        return
      }

      // Student
      this.submitting = true
      verifyLoginPin(this.form.account_name, this.form.account_pwd)
        .then(res => {
          if (res.data.code == 0) {
            const result = res.data.data
            this.$message({ message: 'Login Successful', type: 'success' })
            setUserId(result._id.$id)
            setStudentEmail(this.form.account_name)
            setUserType('student')
            setUserName(
              (result.student_givenName_pinyin || '') + ' ' + (result.student_lastName_pinyin || '')
            )
            setAccountName(result.account_name)
            this.$router.push('/student/dashboard')
          } else {
            this.$message.warning(res.data.msg || 'Login failed')
          }
        })
        .finally(() => { this.submitting = false })
    },
    sendCode() {
      if (!this.form.account_name) {
        this.$message.error('Please input email')
        return
      }
      this.sending = true
      createLoginPinCode(this.form.account_name)
        .then(res => {
          if (res.data.code == 0) {
            this.$message({ message: 'Verification code sent successfully.', type: 'success' })
            this.countdown()
          } else {
            this.$message.warning(res.data.msg || 'Verification code sent failed.')
          }
        })
        .finally(() => { this.sending = false })
    },
    countdown() {
      if (this.remainingTime > 0) {
        this.remainingTime -= 1
        this.timer = setTimeout(this.countdown, 1000)
      } else {
        clearTimeout(this.timer)
        this.remainingTime = 60
      }
    },
  },
}
</script>

<style scoped>
.dsa-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background:
    radial-gradient(ellipse at 20% 10%, #d6e8f8 0%, transparent 50%),
    radial-gradient(ellipse at 80% 90%, #e8eef5 0%, transparent 45%),
    #f4f6f9;
  font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
  position: relative;
}
.dsa-login-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(244, 246, 249, 0.72);
  backdrop-filter: blur(2px);
}
.dsa-login-overlay >>> .dsa-loading {
  min-height: auto;
  padding: 24px;
}
.dsa-login-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 16px;
  padding: 36px 32px 28px;
  box-shadow: 0 8px 28px rgba(24, 95, 165, 0.08);
}
.brand {
  display: flex;
  justify-content: center;
}
.brand-logo {
  height: 50px;
  width: auto;
  display: block;
}
.subtitle {
  margin: 18px 0 20px;
  font-size: 15px;
  color: #4a5568;
}
.tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 22px;
  padding: 4px;
  background: #f4f6f9;
  border-radius: 10px;
}
.tab {
  flex: 1;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 9px 8px;
  font-size: 13px;
  font-weight: 500;
  color: #8a94a3;
  cursor: pointer;
  font-family: inherit;
}
.tab.active {
  background: #fff;
  color: #185FA5;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.field {
  margin-bottom: 16px;
}
.field label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 6px;
}
.field input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 14px;
  font-family: inherit;
  color: #1a202c;
  background: #fff;
  outline: none;
}
.field input:focus {
  border-color: #185FA5;
}
.pin-row {
  display: flex;
  gap: 10px;
}
.pin-row input {
  flex: 1;
}
.btn-send {
  flex-shrink: 0;
  border: 1px solid #185FA5;
  background: #E6F1FB;
  color: #185FA5;
  border-radius: 10px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  min-width: 96px;
}
.btn-send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-login {
  width: 100%;
  margin-top: 8px;
  border: none;
  background: #185FA5;
  color: #fff;
  border-radius: 10px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.btn-login:hover {
  background: #0C447C;
}
.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.hint {
  margin-top: 16px;
  text-align: center;
  font-size: 12.5px;
  color: #8a94a3;
  line-height: 1.45;
}
</style>
